import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import React, { useState } from "react";

import type { AdminUser } from "@/pages/admin/users/columns"; // Adjust path if needed
import type { AppRouter } from "@/server"; // Import AppRouter to get error shape
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { TRPCClientErrorLike } from "@trpc/client"; // Import tRPC error type
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface SendNotificationPayload {
  userId: string;
  message: string;
}

interface SendNotificationDialogProps {
  user: AdminUser | null;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  // Simplify props: pass only mutate function and pending status
  mutate: (
  	variables: SendNotificationPayload,
  	// Update onError to accept a standard Error type
  	options?: { onSuccess?: () => void; onError?: (error: Error) => void },
  ) => void;
  isPending: boolean;
}

export function SendNotificationDialog({
  user,
  isOpen,
  onOpenChange,
  mutate, // Use simplified props
  isPending, // Use simplified props
}: SendNotificationDialogProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (!user) return;
    if (!message.trim()) {
      toast.warning(t("admin.users.page.sendNotificationDialog.messageRequired"));
      return;
    }

    // Call the passed mutate function
    mutate(
      { userId: user.id, message },
      {
        onSuccess: () => {
          setMessage(""); // Clear message on success
          onOpenChange(false); // Close dialog on success
        },
      },
    );
  };

  // Clear message when dialog opens or user changes
  React.useEffect(() => {
    if (isOpen) {
    	setMessage("");
    }
   }, [isOpen]); // Removed 'user' from dependencies as it's not used in the effect

   const userIdentifier = user?.email || user?.username || user?.id || "";

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {t("admin.users.page.sendNotificationDialog.title", {
              userIdentifier,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("admin.users.page.sendNotificationDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="message" className="text-right">
              {t("admin.users.page.sendNotificationDialog.messageLabel")}
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t(
                "admin.users.page.sendNotificationDialog.messagePlaceholder",
              )}
              className="col-span-3"
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSend}
            disabled={isPending || !message.trim()} // Use isPending prop
          >
            {isPending // Use isPending prop
              ? t("common.sending")
              : t("admin.users.page.sendNotificationDialog.sendButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}